import { useEffect } from "react"
import { Loader2, PanelRight, Sparkles } from "lucide-react"
import type { ReactNode, Ref } from "react"
import { ActionHintTooltip } from "@/components/ui/action-hint-tooltip"
import { Button } from "@/components/ui/primitives"
import { ShotPanelReturnButton } from "@/components/shot-panel/shot-panel-return-button"
import { useMediaCacheDownloads } from "@/hooks/use-media-cache-progress"
import { useTranslation } from "@/hooks/use-translation"
import { fetchAgentActivity, agentActivityScopeKey, type AgentActivityScope } from "@/lib/agent-activity-api"
import { cn } from "@/lib/utils"
import { useAgentActivityStore } from "@/stores/agent-activity-store"
import { useAgentColumnStore } from "@/stores/agent-column-store"
import { useShotPanelStore } from "@/stores/shot-panel-store"
import { useUiStore } from "@/stores/ui-store"
import { useWorkspaceJobsStore } from "@/stores/workspace-jobs-store"

/** Root chrome classes for the fullscreen toolbar bar (AppShell owns the shell). */
export const WORKSPACE_TOOLBAR_ROOT_CLASS =
  "flex w-full max-w-full shrink-0 flex-nowrap items-center justify-between gap-3 border-b border-ui-border py-2 pr-4 pl-12 md:px-4 dark:bg-zinc-900/50 light:bg-ui-bg-elevated/50"

export const WORKSPACE_TOOLBAR_LEFT_CLASS =
  "flex min-w-0 flex-1 flex-nowrap items-center gap-x-4 overflow-hidden text-xs text-ui-text-secondary"

type ToolbarJobsPanelActionsProps = {
  actionsSlotRef?: Ref<HTMLDivElement>
  /** Agents queue scope (project workspace or company admin). */
  activityScope?: AgentActivityScope | null
  /** Bump with cable / job events so badge refreshes without opening Agents. */
  refreshNonce?: number
}

/** Jobs spinner + context panel — always rendered by AppShell, never via page portal. */
export function ToolbarJobsPanelActions({
  actionsSlotRef,
  activityScope = null,
  refreshNonce = 0,
}: ToolbarJobsPanelActionsProps = {}) {
  const { t } = useTranslation(["layout"])
  const { jobsOpen, setJobsOpen, drawerOpen, setDrawerOpen, setDrawerTab, drawerTab } = useUiStore()
  const hasActiveJobs = useWorkspaceJobsStore((s) =>
    s.jobs.some((j) => j.status === "queued" || j.status === "processing"),
  )
  const jobsHeaderActive = jobsOpen || (drawerOpen && drawerTab === "jobs_history")
  const scopeKey = activityScope ? agentActivityScopeKey(activityScope) : null
  const readyUnreadCount = useAgentActivityStore((s) => (s.scopeKey === scopeKey ? s.readyUnreadCount : 0))
  const setReadyUnreadCount = useAgentActivityStore((s) => s.setReadyUnreadCount)
  const resetActivity = useAgentActivityStore((s) => s.reset)
  const shotPanelOpen = useShotPanelStore((s) => s.open)
  const agentSection = useAgentColumnStore((state) => state.section)
  const agentCollapsed = useAgentColumnStore((state) => state.collapsed)
  const agentBusy = useAgentColumnStore((state) => state.busy)
  const agentUnreadPreview = useAgentColumnStore((state) => state.unreadPreview)
  const toggleAgentColumn = useAgentColumnStore((state) => state.toggle)
  const agentUnread = agentSection != null && agentCollapsed && agentUnreadPreview !== null
  const agentToggleLabel = t(agentCollapsed ? "shell.header.agentColumnShow" : "shell.header.agentColumnHide")
  const agentTooltipLabel = agentUnread ? agentUnreadPreview || t("shell.header.agentReply") : agentToggleLabel

  useEffect(() => {
    if (!activityScope) {
      resetActivity()
      return
    }
    let cancelled = false
    void fetchAgentActivity(activityScope, 1)
      .then((data) => {
        if (!cancelled) setReadyUnreadCount(activityScope, data.counts.ready_unread)
      })
      .catch(() => {
        /* badge best-effort */
      })
    return () => {
      cancelled = true
    }
  }, [activityScope, scopeKey, refreshNonce, setReadyUnreadCount, resetActivity])

  return (
    <div className="flex flex-none shrink-0 items-center gap-2">
      {shotPanelOpen ? <ShotPanelReturnButton /> : null}
      <div
        ref={actionsSlotRef}
        className={cn("flex items-center gap-1.5 empty:hidden", shotPanelOpen && "md:hidden")}
      />
      <div className={cn("flex items-center gap-1", hasActiveJobs && "ml-2 border-l border-ui-border pl-2")}>
        <DiskSaveIndicator />
        {hasActiveJobs ? (
          <Button
            variant={jobsHeaderActive ? "secondary" : "ghost"}
            size="icon"
            type="button"
            className="h-7 w-7"
            onClick={() => setJobsOpen(!jobsOpen)}
            title={t("shell.header.jobs")}
            aria-label={t("shell.header.jobs")}
          >
            <Loader2 className="h-3.5 w-3.5 animate-spin text-ui-link" />
          </Button>
        ) : null}
        {agentSection ? (
          <span className="hidden lg:inline-flex">
            <ActionHintTooltip
              key={agentUnread ? "agent-reply" : "agent-toggle"}
              label={agentTooltipLabel}
              open={agentUnread ? true : undefined}
              side="bottom"
            >
              <Button
                variant="ghost"
                size="icon"
                type="button"
                className="h-7 w-7 border border-transparent"
                onClick={toggleAgentColumn}
                aria-expanded={!agentCollapsed}
                aria-busy={agentBusy || undefined}
                aria-label={agentToggleLabel}
              >
                {agentBusy ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-ui-link" />
                ) : (
                  <Sparkles className="h-3.5 w-3.5" />
                )}
              </Button>
            </ActionHintTooltip>
          </span>
        ) : null}
        <span className="relative inline-flex">
          <Button
            variant={drawerOpen ? "secondary" : "ghost"}
            size="icon"
            type="button"
            className={cn("h-7 w-7", !drawerOpen && "border border-transparent")}
            onClick={() => {
              if (!drawerOpen) {
                setDrawerTab("agents")
                setDrawerOpen(true)
              } else {
                setDrawerOpen(false)
              }
            }}
            title={t("shell.header.contextAria")}
            aria-label={t("shell.header.contextAria")}
          >
            <PanelRight className="h-3.5 w-3.5" />
          </Button>
          {readyUnreadCount > 0 ? <ToolbarCountBubble count={readyUnreadCount} /> : null}
        </span>
      </div>
    </div>
  )
}

function DiskSaveIndicator() {
  const { t } = useTranslation(["layout"])
  const { visible, percent } = useMediaCacheDownloads()
  if (!visible) return null
  const label = t("shell.header.savingToDisk")
  return (
    <ActionHintTooltip label={label} side="bottom">
      <span
        className="inline-flex h-7 items-center gap-1 px-1 text-[10px] text-ui-text-secondary tabular-nums"
        aria-label={label}
      >
        <Loader2 className="h-3.5 w-3.5 animate-spin text-ui-link" />
        {percent != null ? <span>{percent}%</span> : null}
      </span>
    </ActionHintTooltip>
  )
}

export function ToolbarCountBubble({ count }: { count: number }) {
  return (
    <span className="pointer-events-none absolute -top-1.5 -right-1.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[9px] leading-none font-bold text-ui-text-primary tabular-nums dark:bg-zinc-700 light:bg-clay-3">
      {count > 99 ? "99+" : count}
    </span>
  )
}

type WorkspaceFullscreenToolbarProps = {
  children: ReactNode
  /** Extra controls before Jobs/PanelRight (legacy inline use; prefer left-slot portal). */
  trailingActions?: ReactNode
}

/**
 * @deprecated Prefer portaling left children into AppShell toolbar slot + ToolbarJobsPanelActions in shell.
 * Kept for rare inline uses that still wrap the full bar.
 */
export function WorkspaceFullscreenToolbar({ children, trailingActions }: WorkspaceFullscreenToolbarProps) {
  return (
    <div className={WORKSPACE_TOOLBAR_ROOT_CLASS}>
      <div className={WORKSPACE_TOOLBAR_LEFT_CLASS}>
        {children}
        {trailingActions}
      </div>
      <ToolbarJobsPanelActions />
    </div>
  )
}
